import React, { useRef } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  CertificateAlign,
  CertificateElement,
  CertificatePageSettings,
  CertificateTemplate,
} from "./certificateTypes";
import CertificateTemplateSelector from "./CertificateTemplateSelector";

type CertificateSettingsPanelProps = {
  templates: CertificateTemplate[];
  activeId: string;
  defaultId: string;
  page: CertificatePageSettings;
  elements: CertificateElement[];
  selected: CertificateElement | null;
  onSelectTemplate: (id: string) => void;
  onRename: (name: string) => void;
  onSaveAsNew: () => void;
  onDuplicate: () => void;
  onReset: () => void;
  onSetDefault: () => void;
  onPageChange: (patch: Partial<CertificatePageSettings>) => void;
  onElementChange: (id: string, patch: Partial<CertificateElement>) => void;
  onToggleField: (ids: string[], visible: boolean) => void;
  onSelectElement?: (id: string | null) => void;
  onSave: () => void;
};

const FONT_FAMILIES = [
  "Georgia, 'Times New Roman', serif",
  "'Times New Roman', Times, serif",
  "Inter, sans-serif",
  "Poppins, sans-serif",
  "'Work Sans', sans-serif",
];

function num(value: string, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function CertificateSettingsPanel({
  templates,
  activeId,
  defaultId,
  page,
  elements,
  selected,
  onSelectTemplate,
  onRename,
  onSaveAsNew,
  onDuplicate,
  onReset,
  onSetDefault,
  onPageChange,
  onElementChange,
  onToggleField,
  onSelectElement,
  onSave,
}: CertificateSettingsPanelProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const allIds = elements.map((item) => item.id);

  return (
    <div className="certificate-settings-body">
      <CertificateTemplateSelector
        templates={templates}
        activeId={activeId}
        defaultId={defaultId}
        onSelect={onSelectTemplate}
        onRename={onRename}
        onSaveAsNew={onSaveAsNew}
        onDuplicate={onDuplicate}
        onReset={onReset}
        onSetDefault={onSetDefault}
      />

      <div className="certificate-section">
        <h3>Show / hide fields</h3>
        <p className="certificate-field-hint">
          Click the eye to hide a field on the certificate. Hidden fields are not printed.
        </p>
        <div className="certificate-template-actions" style={{ marginBottom: 10 }}>
          <button
            type="button"
            className="certificate-btn"
            onClick={() => onToggleField(allIds, true)}
          >
            Show all
          </button>
          <button
            type="button"
            className="certificate-btn"
            onClick={() =>
              onToggleField(
                elements
                  .filter((item) =>
                    ["patientDetails", "reason", "recommendationText", "clinicWeb"].includes(
                      item.id
                    )
                  )
                  .map((item) => item.id),
                false
              )
            }
          >
            Hide optional
          </button>
        </div>
        {(["header", "body", "footer"] as const).map((section) => {
          const rows = elements.filter((item) => item.section === section);
          if (rows.length === 0) return null;
          return (
            <div key={section} className="certificate-field-group">
              <h4>{section}</h4>
              {rows.map((item) => (
                <div
                  key={item.id}
                  className={`certificate-field-row${
                    selected?.id === item.id ? " is-selected" : ""
                  }${item.visible ? "" : " is-hidden"}`}
                >
                  <button
                    type="button"
                    className="certificate-field-name"
                    onClick={() => onSelectElement?.(item.id)}
                  >
                    {item.label}
                  </button>
                  <button
                    type="button"
                    className="certificate-visibility-btn"
                    title={item.visible ? "Hide field" : "Show field"}
                    aria-label={item.visible ? `Hide ${item.label}` : `Show ${item.label}`}
                    onClick={() => onToggleField([item.id], !item.visible)}
                  >
                    {item.visible ? (
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <VisibilityOffIcon sx={{ fontSize: 18 }} />
                    )}
                    <span>{item.visible ? "Hide" : "Show"}</span>
                  </button>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="certificate-section">
        <h3>Page</h3>
        <div className="certificate-row">
          <div className="certificate-field">
            <label>Size</label>
            <select
              value={page.size}
              onChange={(event) =>
                onPageChange({
                  size: event.target.value as CertificatePageSettings["size"],
                })
              }
            >
              <option value="A4">A4</option>
              <option value="A5">A5</option>
              <option value="halfLetter">Half Letter (5.5 × 8.5 in)</option>
            </select>
          </div>
          <div className="certificate-field">
            <label>Orientation</label>
            <select
              value={page.orientation}
              onChange={(event) =>
                onPageChange({
                  orientation: event.target.value as CertificatePageSettings["orientation"],
                })
              }
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>
        </div>
        <div className="certificate-field">
          <label>Margins (mm)</label>
          <input
            type="number"
            min={8}
            max={40}
            value={page.marginMm}
            onChange={(event) => onPageChange({ marginMm: num(event.target.value, page.marginMm) })}
          />
        </div>
        <label className="certificate-check">
          <input
            type="checkbox"
            checked={page.showSafeArea}
            onChange={(event) => onPageChange({ showSafeArea: event.target.checked })}
          />
          Show print-safe area
        </label>
      </div>

      <div className="certificate-section">
        <h3>Typography</h3>
        <div className="certificate-field">
          <label>Font family</label>
          <select
            value={page.fontFamily}
            onChange={(event) => onPageChange({ fontFamily: event.target.value })}
          >
            {FONT_FAMILIES.map((font) => (
              <option key={font} value={font}>
                {font.split(",")[0].replace(/'/g, "")}
              </option>
            ))}
          </select>
        </div>
        <div className="certificate-row">
          <div className="certificate-field">
            <label>Font size (pt)</label>
            <input
              type="number"
              min={8}
              max={28}
              value={page.fontSize}
              onChange={(event) => onPageChange({ fontSize: num(event.target.value, page.fontSize) })}
            />
          </div>
          <div className="certificate-field">
            <label>Weight</label>
            <select
              value={page.fontWeight}
              onChange={(event) => onPageChange({ fontWeight: num(event.target.value, page.fontWeight) })}
            >
              <option value={400}>Regular</option>
              <option value={500}>Medium</option>
              <option value={600}>Semibold</option>
              <option value={700}>Bold</option>
            </select>
          </div>
        </div>
        <div className="certificate-row">
          <div className="certificate-field">
            <label>Line height</label>
            <input
              type="number"
              min={1}
              max={2.4}
              step={0.05}
              value={page.lineHeight}
              onChange={(event) =>
                onPageChange({ lineHeight: num(event.target.value, page.lineHeight) })
              }
            />
          </div>
          <div className="certificate-field">
            <label>Alignment</label>
            <select
              value={page.textAlign}
              onChange={(event) =>
                onPageChange({ textAlign: event.target.value as CertificateAlign })
              }
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
        <div className="certificate-field">
          <label>Font style</label>
          <div className="certificate-style-toggles">
            <button
              type="button"
              className={`certificate-style-toggle${page.fontWeight >= 700 ? " is-active" : ""}`}
              title="Bold"
              aria-pressed={page.fontWeight >= 700}
              onClick={() =>
                onPageChange({ fontWeight: page.fontWeight >= 700 ? 400 : 700 })
              }
            >
              B
            </button>
            <button
              type="button"
              className={`certificate-style-toggle is-italic${page.italic ? " is-active" : ""}`}
              title="Italic"
              aria-pressed={Boolean(page.italic)}
              onClick={() => onPageChange({ italic: !page.italic })}
            >
              I
            </button>
            <button
              type="button"
              className={`certificate-style-toggle is-underline${page.underline ? " is-active" : ""}`}
              title="Underline"
              aria-pressed={Boolean(page.underline)}
              onClick={() => onPageChange({ underline: !page.underline })}
            >
              U
            </button>
          </div>
        </div>
      </div>

      <div className="certificate-section" ref={headerRef}>
        <h3>Header</h3>
        <div className="certificate-field">
          <label>Logo</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                if (typeof reader.result === "string") {
                  onPageChange({ logoSrc: reader.result });
                }
              };
              reader.readAsDataURL(file);
            }}
          />
        </div>
        <div className="certificate-field">
          <label>Logo size (mm)</label>
          <input
            type="number"
            min={12}
            max={60}
            value={page.logoSizeMm}
            onChange={(event) => {
              const logoSizeMm = num(event.target.value, page.logoSizeMm);
              onPageChange({ logoSizeMm });
              onElementChange("logo", { width: logoSizeMm, height: logoSizeMm });
            }}
          />
        </div>
        <div className="certificate-field">
          <label>Header alignment</label>
          <select
            value={page.headerAlign}
            onChange={(event) => {
              const headerAlign = event.target.value as CertificateAlign;
              onPageChange({ headerAlign });
              ["clinicName", "clinicAddress", "clinicContact", "clinicWeb"].forEach((id) =>
                onElementChange(id, { align: headerAlign })
              );
            }}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
        {["clinicName", "clinicAddress", "clinicContact", "clinicWeb"].map((id) => {
          const el = elements.find((item) => item.id === id);
          if (!el) return null;
          return (
            <div className="certificate-field" key={id}>
              <label>{el.label}</label>
              <input
                value={el.content}
                onChange={(event) => onElementChange(id, { content: event.target.value })}
              />
            </div>
          );
        })}
      </div>

      <div className="certificate-section">
        <h3>Body</h3>
        <div className="certificate-row">
          <div className="certificate-field">
            <label>Field spacing</label>
            <input
              type="number"
              min={0}
              max={16}
              value={page.fieldSpacing}
              onChange={(event) =>
                onPageChange({ fieldSpacing: num(event.target.value, page.fieldSpacing) })
              }
            />
          </div>
          <div className="certificate-field">
            <label>Paragraph spacing</label>
            <input
              type="number"
              min={0}
              max={16}
              value={page.paragraphSpacing}
              onChange={(event) =>
                onPageChange({ paragraphSpacing: num(event.target.value, page.paragraphSpacing) })
              }
            />
          </div>
        </div>
        <div className="certificate-field">
          <label>Label / value style</label>
          <select
            value={page.labelValueStyle}
            onChange={(event) =>
              onPageChange({
                labelValueStyle: event.target.value as CertificatePageSettings["labelValueStyle"],
              })
            }
          >
            <option value="inline">Inline</option>
            <option value="stacked">Stacked</option>
          </select>
        </div>
      </div>

      <div className="certificate-section" ref={footerRef}>
        <h3>Footer</h3>
        <div className="certificate-field">
          <label>Footer text</label>
          <input
            value={page.footerText}
            onChange={(event) => {
              onPageChange({ footerText: event.target.value });
              onElementChange("thankYou", { content: event.target.value });
            }}
          />
        </div>
        {["doctorName", "qualifications", "designation", "slmcNumber"].map((id) => {
          const el = elements.find((item) => item.id === id);
          if (!el) return null;
          return (
            <div className="certificate-field" key={id}>
              <label>{el.label}</label>
              <input
                value={el.content}
                onChange={(event) => onElementChange(id, { content: event.target.value })}
              />
            </div>
          );
        })}
      </div>

      {selected ? (
        <div className="certificate-section">
          <h3>Selected element</h3>
          <p className="mb-3 text-xs text-slate-500">{selected.label}</p>
          <div className="certificate-row">
            <div className="certificate-field">
              <label>X (mm)</label>
              <input
                type="number"
                value={Math.round(selected.x * 10) / 10}
                onChange={(event) =>
                  onElementChange(selected.id, { x: num(event.target.value, selected.x) })
                }
              />
            </div>
            <div className="certificate-field">
              <label>Y (mm)</label>
              <input
                type="number"
                value={Math.round(selected.y * 10) / 10}
                onChange={(event) =>
                  onElementChange(selected.id, { y: num(event.target.value, selected.y) })
                }
              />
            </div>
          </div>
          <div className="certificate-row">
            <div className="certificate-field">
              <label>Width (mm)</label>
              <input
                type="number"
                value={Math.round(selected.width * 10) / 10}
                onChange={(event) =>
                  onElementChange(selected.id, { width: num(event.target.value, selected.width) })
                }
              />
            </div>
            <div className="certificate-field">
              <label>Height (mm)</label>
              <input
                type="number"
                value={Math.round(selected.height * 10) / 10}
                onChange={(event) =>
                  onElementChange(selected.id, { height: num(event.target.value, selected.height) })
                }
              />
            </div>
          </div>
          {selected.type === "text" ? (
            <>
              <div className="certificate-field">
                <label>Content</label>
                <textarea
                  value={selected.content}
                  onChange={(event) =>
                    onElementChange(selected.id, { content: event.target.value })
                  }
                />
              </div>
              <div className="certificate-field">
                <label>Font family</label>
                <select
                  value={selected.fontFamily || ""}
                  onChange={(event) =>
                    onElementChange(selected.id, {
                      fontFamily: event.target.value || undefined,
                    })
                  }
                >
                  <option value="">Use page default</option>
                  {FONT_FAMILIES.map((font) => (
                    <option key={font} value={font}>
                      {font.split(",")[0].replace(/'/g, "")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="certificate-row">
                <div className="certificate-field">
                  <label>Font size (pt)</label>
                  <input
                    type="number"
                    min={8}
                    max={36}
                    value={selected.fontSize}
                    onChange={(event) =>
                      onElementChange(selected.id, {
                        fontSize: num(event.target.value, selected.fontSize),
                      })
                    }
                  />
                </div>
                <div className="certificate-field">
                  <label>Weight</label>
                  <select
                    value={selected.fontWeight}
                    onChange={(event) =>
                      onElementChange(selected.id, {
                        fontWeight: num(event.target.value, selected.fontWeight),
                      })
                    }
                  >
                    <option value={400}>Regular</option>
                    <option value={500}>Medium</option>
                    <option value={600}>Semibold</option>
                    <option value={700}>Bold</option>
                  </select>
                </div>
              </div>
              <div className="certificate-field">
                <label>Font style</label>
                <div className="certificate-style-toggles">
                  <button
                    type="button"
                    className={`certificate-style-toggle${
                      selected.fontWeight >= 700 ? " is-active" : ""
                    }`}
                    title="Bold"
                    aria-pressed={selected.fontWeight >= 700}
                    onClick={() =>
                      onElementChange(selected.id, {
                        fontWeight: selected.fontWeight >= 700 ? 400 : 700,
                      })
                    }
                  >
                    B
                  </button>
                  <button
                    type="button"
                    className={`certificate-style-toggle is-italic${
                      (selected.italic ?? page.italic) ? " is-active" : ""
                    }`}
                    title="Italic"
                    aria-pressed={Boolean(selected.italic ?? page.italic)}
                    onClick={() =>
                      onElementChange(selected.id, {
                        italic: !(selected.italic ?? page.italic),
                      })
                    }
                  >
                    I
                  </button>
                  <button
                    type="button"
                    className={`certificate-style-toggle is-underline${
                      (selected.underline ?? page.underline) ? " is-active" : ""
                    }`}
                    title="Underline"
                    aria-pressed={Boolean(selected.underline ?? page.underline)}
                    onClick={() =>
                      onElementChange(selected.id, {
                        underline: !(selected.underline ?? page.underline),
                      })
                    }
                  >
                    U
                  </button>
                </div>
              </div>
              <div className="certificate-field">
                <label>Alignment</label>
                <select
                  value={selected.align}
                  onChange={(event) =>
                    onElementChange(selected.id, {
                      align: event.target.value as CertificateAlign,
                    })
                  }
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </>
          ) : null}
          <button
            type="button"
            className="certificate-btn"
            onClick={() => onElementChange(selected.id, { visible: !selected.visible })}
          >
            {selected.visible ? "Hide this field" : "Show this field"}
          </button>
          <label className="certificate-check">
            <input
              type="checkbox"
              checked={selected.visible}
              onChange={(event) =>
                onElementChange(selected.id, { visible: event.target.checked })
              }
            />
            Visible on certificate
          </label>
        </div>
      ) : (
        <div className="certificate-section">
          <h3>Selection</h3>
          <p className="m-0 text-sm text-slate-500">
            Click an element on the certificate to edit position, size, and typography.
          </p>
        </div>
      )}

      <div className="certificate-actions">
        <button type="button" className="certificate-btn certificate-btn-primary" onClick={onSave}>
          Save template
        </button>
        <button type="button" className="certificate-btn" onClick={onReset}>
          Reset
        </button>
        <button
          type="button"
          className="certificate-btn"
          onClick={() => headerRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Edit header
        </button>
        <button
          type="button"
          className="certificate-btn"
          onClick={() => footerRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Edit footer
        </button>
      </div>
    </div>
  );
}

export default CertificateSettingsPanel;
